import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  RadioGroup,
  ToggleButtonGroup,
} from '@mui/material';
import React from 'react';
import type { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import type Investor from '../../models/Investor';
import type IRRDataPoint from '../../models/IRRDataPoint';
import { IRRUpdate } from '../../models/IRRUpdate';
import type { FundUpdateChartType } from '../../pages/fundUpdate/fundUpdateUtils';
import ROEGraphDisclaimer from '../ROEGraphDisclaimer';
import { Palette } from 'Palette';
import {
  StyledButtonGroup,
  StyledToggleButton,
  StyledToggleButtonGroup,
} from '../styles/StyledButtons';
import { StyledCurrencyInput, StyledRadio } from '../styles/StyledInputs';
import { StyledHeading, StyledTypography } from '../styles/styles';
import FundUpdateCharts from './FundUpdateCharts';

export interface FundUpdateCashflowSectionProps {
  irrUpdates: IRRUpdate[];
  selectedLabel: string;
  chartType: FundUpdateChartType;
  chartData: IRRDataPoint[];
  chartDataLoading: boolean;
  combinedEntityView: boolean;
  allocationsData?: InvestorAllocationsResponse;
  selectedEntityIds: Set<string>;
  fundSize?: number;
  investmentKey: number;
  investor?: Investor;
  simulatedInvestment?: number;
  simulatedInvestmentError?: string;
  onPeriodChange: (label: string) => void;
  onChartTypeChange: (chartType: FundUpdateChartType) => void;
  onCombinedEntityViewChange: (combined: boolean) => void;
  onSimulatedInvestmentChange: (value?: string) => void;
  /** Admin's Fund Management preview trims this section down (smaller heading, compact toggle, no investor-facing disclaimer). */
  canEdit?: boolean;
}

// StyledToggleButton's unselected hover (shared with every other caller) lands
// on a dark background with dark text in this tone1 section — readable
// elsewhere, not here. Scoped to the admin Cumulative/Discrete toggle only,
// matching the fix already applied to ContentEditorToolbar's View toggle.
const TOGGLE_HOVER_FIX_SX = {
  '@media (hover: hover)': {
    '&:hover:not(.Mui-selected)': {
      backgroundColor: Palette.tone1.buttonBg,
      color: Palette.tone1.buttonText,
    },
  },
};

// Mirrors StyledToggleButton's own selected-border gap fix from
// ContentEditorToolbar, for the same reason: a continuous outline around the
// compact admin toggle group.
const TOGGLE_SELECTED_BORDER_SX = {
  '&.Mui-selected': {
    border: `1px solid ${Palette.default.inactiveOrCancelButtonText}`,
  },
};

// `mini`'s own horizontal padding (0.25rem) reads too tight for this toggle,
// and a shared fixed width means the longer "Cumulative" label eats most of
// it. 6.75rem gives "Cumulative" a comfortable but not oversized margin.
//
// The admin toggle is wrapped in a plain MUI ToggleButtonGroup (see below), not
// StyledToggleButtonGroup (the investor toggle keeps it) — that wrapper's own
// "segmented" styles set `width: 100%` + `flex: 1 1 auto` on each button at the
// lg breakpoint,
// which fought any width set here for the same CSS property: sometimes losing
// (both buttons shrink to a sliver once the row is tight — confirmed via
// DevTools, a 152px flex-basis collapsing to 68px), sometimes "winning" in a
// way that still left `width: 100%` driving the flex-basis, so the button
// wouldn't shrink AND claimed the full row, overflowing past the panel.
// ContentEditorToolbar's View toggle (the one this is modelled on) never had
// this problem for the same reason: it also skips StyledToggleButtonGroup.
const TOGGLE_WIDTH_SX = { width: '6.75rem', justifyContent: 'center', marginTop: 0 };

const FundUpdateCashflowSection: React.FC<FundUpdateCashflowSectionProps> = ({
  irrUpdates,
  selectedLabel,
  chartType,
  chartData,
  chartDataLoading,
  combinedEntityView,
  allocationsData,
  selectedEntityIds,
  fundSize,
  investmentKey,
  investor,
  simulatedInvestment,
  simulatedInvestmentError,
  onPeriodChange,
  onChartTypeChange,
  onCombinedEntityViewChange,
  onSimulatedInvestmentChange,
  canEdit = false,
}) => {
  const showEntityViewToggle =
    Boolean(investor) && Boolean(allocationsData) && selectedEntityIds.size > 1;

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ mt: canEdit ? 3 : undefined }}
      >
        <StyledHeading headerVariant={canEdit ? 'subheading' : 'heading2'}>
          Projected Cashflow ({chartType})
        </StyledHeading>
      </Box>

      <Box sx={canEdit ? { display: 'flex', alignItems: 'center' } : { display: 'contents' }}>
        <Box sx={{ flex: 1, mt: canEdit ? '6px' : undefined }}>
          {irrUpdates.length > 0 && (
            <FormControl component="fieldset">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: canEdit ? 'row' : 'column',
                  alignItems: canEdit ? 'center' : undefined,
                  gap: canEdit ? 1.5 : undefined,
                }}
              >
                <StyledHeading
                  headerVariant="subheading"
                  reduceBottomPadding={!canEdit}
                  sx={canEdit ? { m: 0, p: 0, whiteSpace: 'nowrap' } : undefined}
                >
                  Select Update Period
                </StyledHeading>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <RadioGroup
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '1rem',
                      opacity: chartDataLoading ? 0.5 : 1,
                      pointerEvents: chartDataLoading ? 'none' : 'auto',
                    }}
                    name="update"
                    value={selectedLabel}
                    onChange={(e) => onPeriodChange(e.target.value)}
                  >
                    {irrUpdates.map((update) => (
                      <FormControlLabel
                        key={`${update.month}-${update.label}`}
                        value={update.label}
                        control={<StyledRadio size="small" />}
                        label={update.label}
                      />
                    ))}
                  </RadioGroup>
                  {chartDataLoading && (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: Palette['default'].buttonBg,
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </FormControl>
          )}
        </Box>
        <StyledButtonGroup justifyContent="flex-end" sx={{ mt: 0 }}>
          {showEntityViewToggle && (
            <StyledToggleButtonGroup
              size="small"
              exclusive
              segmented={true}
              value={combinedEntityView ? 'combined' : 'per_entity'}
              onChange={(_, val) => val && onCombinedEntityViewChange(val === 'combined')}
              sx={{
                mb: { md: 2 },
                maxWidth: { xs: '100%' },
              }}
            >
              <StyledToggleButton value="combined">Combined</StyledToggleButton>
              <StyledToggleButton value="per_entity">Per Entity</StyledToggleButton>
            </StyledToggleButtonGroup>
          )}
          {canEdit ? (
            <ToggleButtonGroup
              exclusive
              value={chartType}
              onChange={(_, val) => val && onChartTypeChange(val)}
              size="small"
              sx={{ mb: 0 }}
            >
              <StyledToggleButton
                value="Cumulative"
                mini
                sx={[TOGGLE_HOVER_FIX_SX, TOGGLE_SELECTED_BORDER_SX, TOGGLE_WIDTH_SX]}
              >
                Cumulative
              </StyledToggleButton>
              <StyledToggleButton
                value="Discrete"
                mini
                sx={[TOGGLE_HOVER_FIX_SX, TOGGLE_SELECTED_BORDER_SX, TOGGLE_WIDTH_SX]}
              >
                Discrete
              </StyledToggleButton>
            </ToggleButtonGroup>
          ) : (
            <StyledToggleButtonGroup
              exclusive
              value={chartType}
              onChange={(_, val) => val && onChartTypeChange(val)}
              sx={{ mb: { md: 2 } }}
            >
              <StyledToggleButton value="Cumulative">Cumulative</StyledToggleButton>
              <StyledToggleButton value="Discrete">Discrete</StyledToggleButton>
            </StyledToggleButtonGroup>
          )}
        </StyledButtonGroup>
      </Box>

      <FundUpdateCharts
        chartType={chartType}
        chartData={chartData}
        combinedEntityView={combinedEntityView}
        allocationsData={allocationsData}
        selectedEntityIds={selectedEntityIds}
        fundSize={fundSize}
        investmentKey={investmentKey}
        canEdit={canEdit}
      />

      {!canEdit && <ROEGraphDisclaimer />}

      {!investor && (
        <Box sx={{ mb: 2, mt: canEdit ? 3 : undefined }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: canEdit ? 'row' : 'column',
              alignItems: canEdit ? 'center' : undefined,
              gap: canEdit ? 1.5 : undefined,
            }}
          >
            <StyledHeading
              headerVariant="subheading"
              reduceBottomPadding={!canEdit}
              sx={canEdit ? { m: 0, p: 0, whiteSpace: 'nowrap' } : undefined}
            >
              Simulated Investment Amount
            </StyledHeading>
            <StyledCurrencyInput
              value={simulatedInvestment?.toString() || ''}
              allowDecimals={false}
              allowNegativeValue={false}
              placeholder="$ Simulated Investment"
              onValueChange={(val) => onSimulatedInvestmentChange(val?.toString())}
            />
          </Box>
          {simulatedInvestmentError && (
            <StyledTypography variant="body2" tone="tone1" sx={{ mt: 1 }} className="input-error">
              {simulatedInvestmentError}
            </StyledTypography>
          )}
        </Box>
      )}
    </>
  );
};

export default FundUpdateCashflowSection;
